import { useMutation } from "@apollo/client";
import {
  Anchor,
  Box,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconExclamationMark, IconMail } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import {
  SendPasswordResetEmailMutation,
  SendPasswordResetEmailMutationVariables,
} from "../../../../gql/__generated__/graphql";
import { SEND_PASSWORD_RESET_EMAIL } from "../../../apis/lambda/GraphQL/User/mutations";

interface ForgotPasswordFormValues {
  email: string;
}

function ForgotPasswordPage() {
  const form = useForm<ForgotPasswordFormValues>({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) => {
        if (!value) return "Email is required";
        if (!/\S+@\S+\.\S+/.test(value)) return "Email is invalid";
        return null;
      },
    },
  });

  const [sendPasswordResetEmail, { loading }] = useMutation<
    SendPasswordResetEmailMutation,
    SendPasswordResetEmailMutationVariables
  >(SEND_PASSWORD_RESET_EMAIL, {
    onCompleted: () => {
      notifications.show({
        title: "Reset email sent",
        message: "Please check your email for password reset instructions.",
        color: "green",
      });
    },
    onError: (_error) => {
      notifications.show({
        title: "Error",
        message: "Failed to send reset email. Please try again.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
    },
  });

  function handleSubmit(values: ForgotPasswordFormValues) {
    sendPasswordResetEmail({
      variables: {
        email: values.email,
      },
    }).catch(() => {
      // Error is handled by the onError callback in useMutation
    });
  }

  return (
    <Box
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container size="xs" w="100%">
        <Stack gap="xl" align="stretch" w="100%">
          <Title
            order={1}
            ta="center"
            style={{
              background:
                "linear-gradient(45deg, var(--mantine-color-tertiary-6), var(--mantine-color-yellow-6))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1.3,
            }}
          >
            Forgot Password
          </Title>

          <Paper p="xl" radius="lg" shadow="md" withBorder>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="lg">
                <TextInput
                  label="Email"
                  placeholder="Enter your email"
                  leftSection={<IconMail size={16} />}
                  {...form.getInputProps("email")}
                />

                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  color="secondary"
                  loading={loading}
                >
                  Send Reset Email
                </Button>
              </Stack>
            </form>
          </Paper>

          <Group justify="center">
            <Text size="sm" c="gray.6">
              Remembered your password?{" "}
              <Anchor component={Link} to="/login" c="secondary" fw={600}>
                Log In
              </Anchor>
            </Text>
          </Group>
        </Stack>
      </Container>
    </Box>
  );
}

export default ForgotPasswordPage;
